import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Reveal, Lines, SectionTag } from "../site/Motion";
import { IMG } from "../../lib/images";

const CHAPTERS = [
  { n: "01", title: "Nata per lo spazio urbano", text: "Le auto tradizionali vengono ridotte o elettrificate per entrare in città. SWAPA nasce dal foglio bianco: l'architettura è disegnata intorno allo spazio reale della città." },
  { n: "02", title: "Due posti, una linea", text: "L'abitacolo tandem ONE+1 porta due persone in 1009 mm di larghezza. Meno ingombro, più libertà di movimento." },
  { n: "03", title: "Zero compromessi", text: "Costruzione automobilistica MONOQ, dotazione completa di serie, propulsione elettrica. Il formato è compatto, l'esperienza no." },
];

const GALLERY = [
  { id: "duomo", img: IMG.manifestoDuomo, alt: "SWAPA grigia con conducente in abito scuro davanti al Duomo di Milano", caption: "SWAPA™ — Milano Duomo" },
  { id: "scuola", img: IMG.manifestoScuola, alt: "SWAPA blu vista posteriore durante l'accompagnamento a scuola", caption: "SWAPA™ — School Run" },
  { id: "parcheggio", img: IMG.manifestoParcheggio, alt: "SWAPA parcheggiata in uno stallo tra scooter e moto in centro città", caption: "SWAPA™ — Parking" },
];

export function Manifesto() {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], ["-8%", "8%"]);

  return (
    <section id="manifesto" data-testid="manifesto-section" ref={ref} className="relative overflow-hidden bg-ink py-28 sm:py-40">
      <div className="mx-auto max-w-[1600px] px-5 sm:px-10">
        <SectionTag index="02" label="Manifesto urbano" />

        <h2 className="mt-10 font-display font-extrabold uppercase leading-[0.92] tracking-tight">
          <Lines lines={["Made for the city."]} className="text-4xl sm:text-6xl lg:text-7xl text-white" />
          <Lines lines={["Not adapted to it."]} delay={0.15} className="text-4xl sm:text-6xl lg:text-7xl text-outline" />
        </h2>

        <div className="mt-16 grid gap-14 lg:grid-cols-2 lg:gap-20">
          <Reveal className="relative order-2 lg:order-1" y={60}>
            <motion.figure className="relative" style={reduce ? {} : { y: imgY }}>
              <img
                src={IMG.manifesto}
                alt="SWAPA in studio, livrea grigia con dettagli gialli"
                className="h-auto w-full object-contain"
                loading="lazy"
                data-testid="manifesto-img-main"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent" aria-hidden="true" />
              <p className="absolute bottom-5 left-5 font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-300">
                SWAPA™ — Prêt-à-Drive
              </p>
            </motion.figure>
          </Reveal>

          <div className="order-1 flex flex-col justify-center gap-12 lg:order-2">
            {CHAPTERS.map((c, i) => (
              <Reveal key={c.n} delay={i * 0.12} className="group border-l border-white/10 pl-6 transition-colors duration-500 hover:border-swapa-yellow sm:pl-8">
                <span className="font-mono text-xs text-swapa-yellow">{c.n} /</span>
                <h3 className="mt-2 font-display text-xl font-bold uppercase tracking-tight text-white sm:text-2xl">{c.title}</h3>
                <p className="mt-3 max-w-md text-sm leading-relaxed text-zinc-400">{c.text}</p>
              </Reveal>
            ))}
          </div>
        </div>

        <div data-testid="manifesto-gallery" className="mt-14 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 sm:mt-20 sm:grid sm:grid-cols-3 sm:gap-6 sm:overflow-visible sm:pb-0">
          {GALLERY.map((g, i) => (
            <Reveal key={g.id} delay={i * 0.1} className="w-[80%] shrink-0 snap-start sm:w-auto">
              <figure className="group relative overflow-hidden border border-white/10">
                <img
                  src={g.img}
                  alt={g.alt}
                  className="aspect-[4/3] w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  loading="lazy"
                  data-testid={`manifesto-img-${g.id}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/45 via-transparent to-transparent" aria-hidden="true" />
                <p className="absolute bottom-4 left-4 font-mono text-[10px] uppercase tracking-[0.22em] text-zinc-200">
                  {g.caption}
                </p>
              </figure>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
